<template>
  <section class="p-2 sanctions-bulk-upload">
    <v-card>
      <v-card-title class="d-flex justify-space-between p-0 m-0">
        <div class="ps-2 headingFontsize">Sanctions Bulk Upload</div>
        <bread-crumb :bread-crumbs="['Sanctions Bulk Upload']" />
      </v-card-title>
      <v-divider class="p-0 m-0 primary--text"></v-divider>

      <v-dialog v-model="uploadDialog" persistent max-width="1200">
        <v-card>
          <v-card-title class="grey lighten-3 py-3">
            <span class="subtitleFontsize">Upload sanctions file</span>
            <v-spacer></v-spacer>
            <v-icon
              :disabled="isValidating || isCommitting"
              @click="closeUploadDialog"
            >
              mdi-close
            </v-icon>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-form ref="bulkUploadForm" v-model="valid">
              <v-row align="start">
                <v-col cols="12" md="7" lg="6">
                  <v-file-input
                    ref="fileInput"
                    v-model="document"
                    :rules="fileRules"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    chips
                    clearable
                    dense
                    outlined
                    prepend-icon=""
                    label="Sanctions Excel file"
                    placeholder="Choose an Excel file"
                    show-size
                    @change="resetValidation"
                  >
                    <template v-slot:append>
                      <v-icon color="primary">mdi-cloud-upload-outline</v-icon>
                    </template>
                  </v-file-input>
                  <span class="text-caption grey--text">
                    Excel (.xlsx or .xls), maximum 10 MB
                  </span>
                </v-col>
                <v-col cols="12" md="5" lg="6" class="upload-actions">
                  <v-btn
                    color="primary"
                    depressed
                    small
                    :disabled="!document || isValidating || isCommitting"
                    :loading="isValidating"
                    @click="validateFile"
                  >
                    <v-icon left small>mdi-file-check-outline</v-icon>
                    Validate file
                  </v-btn>
                  <v-btn
                    class="ml-2"
                    color="success"
                    depressed
                    small
                    :disabled="!canCommit || isValidating || isCommitting"
                    @click="commitDialog = true"
                  >
                    <v-icon left small>mdi-database-import-outline</v-icon>
                    Commit upload
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>

            <template v-if="hasValidationResult">
              <v-alert
                v-if="!hasWritableRows"
                dense
                text
                type="warning"
                class="mt-3 mb-0"
              >
                No INSERT or UPDATE records are available. This validation
                result cannot be committed.
              </v-alert>
              <v-alert
                v-else-if="!uploadToken"
                dense
                text
                type="error"
                class="mt-3 mb-0"
              >
                Validation did not return the upload token required to commit
                this file.
              </v-alert>
              <v-row class="mt-3">
                <v-col
                  v-for="card in validationCards"
                  :key="card.status"
                  cols="6"
                  sm="4"
                  md="2"
                >
                  <v-card
                    outlined
                    class="result-card pa-3"
                    :class="{ 'result-card--active': previewStatus === card.status }"
                    @click="setPreviewStatus(card.status)"
                  >
                    <div class="text-caption grey--text text-uppercase">
                      {{ card.label }}
                    </div>
                    <div class="text-h5 font-weight-medium" :class="card.color + '--text'">
                      {{ card.count }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-card outlined class="mt-4">
                <v-card-title class="py-2">
                  <span class="subtitleFontsize">Validation preview</span>
                  <v-chip
                    v-if="previewStatus !== 'ALL'"
                    small
                    close
                    class="ml-2"
                    @click:close="previewStatus = 'ALL'"
                  >
                    {{ previewStatus }}
                  </v-chip>
                  <v-btn
                    v-if="failedExcelUrl"
                    :href="failedExcelUrl"
                    target="_blank"
                    download
                    text
                    small
                    color="error"
                    class="ml-2"
                  >
                    <v-icon left small>mdi-file-excel-outline</v-icon>
                    Download failed records
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="previewSearch"
                    append-icon="mdi-magnify"
                    placeholder="Search preview"
                    dense
                    hide-details
                    single-line
                    class="table-search"
                  ></v-text-field>
                </v-card-title>
                <v-data-table
                  :headers="previewHeaders"
                  :items="filteredPreviewRows"
                  :search="previewSearch"
                  :items-per-page="10"
                  fixed-header
                  height="42vh"
                  no-data-text="No records returned for this status"
                >
                  <template v-slot:[`item.__status`]="{ item }">
                    <v-chip small outlined :color="statusColor(item.__status)">
                      {{ item.__status || '-' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card>
            </template>

            <v-card v-else outlined class="empty-preview mt-4 pa-8 text-center">
              <v-icon size="42" color="grey lighten-1">
                mdi-table-search
              </v-icon>
              <div class="mt-2 grey--text">
                Validation results will appear here.
              </div>
            </v-card>
          </v-card-text>
        </v-card>
      </v-dialog>

      <div class="pa-4">
            <v-card-title class="px-0 pt-0">
              <v-btn
                color="primary"
                depressed
                small
                @click="openUploadDialog"
              >
                <v-icon left small>mdi-cloud-upload-outline</v-icon>
                Bulk Upload
              </v-btn>
              <v-btn
                href="/sanctionsBulkUpload.xlsx"
                download="sanctionsBulkUpload.xlsx"
                color="primary"
                outlined
                small
                class="ml-2"
              >
                <v-icon left small>mdi-file-excel-outline</v-icon>
                Download Sample Excel
              </v-btn>
              <v-btn
                color="primary"
                text
                small
                class="ml-2"
                :loading="isHistoryLoading"
                @click="loadHistory"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="historySearch"
                append-icon="mdi-magnify"
                placeholder="Search upload history"
                dense
                hide-details
                single-line
                class="table-search"
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="historyHeaders"
              :items="historyItems"
              :search="historySearch"
              :loading="isHistoryLoading"
              :items-per-page="historyLimit"
              hide-default-footer
              fixed-header
              height="50vh"
              loading-text="Loading upload history..."
              no-data-text="No sanctions bulk uploads found"
            >
              <template v-slot:[`item.document_name`]="{ item }">
                <a
                  v-if="item.document_path"
                  :href="item.document_path"
                  target="_blank"
                  download
                  class="count-link"
                >
                  <v-icon x-small color="primary">mdi-download</v-icon>
                  {{ item.document_name }}
                </a>
                <span v-else>{{ item.document_name || '-' }}</span>
              </template>
              <template v-slot:[`item.no_of_success`]="{ item }">
                <a class="count-link" @click="openSuccessfulRecords(item)">
                  {{ item.no_of_success }}
                </a>
              </template>
              <template v-slot:[`item.no_of_failure`]="{ item }">
                <span class="error--text">{{ item.no_of_failure }}</span>
              </template>
              <template v-slot:[`item.failed_document_path`]="{ item }">
                <a
                  v-if="item.failed_document_path"
                  :href="item.failed_document_path"
                  target="_blank"
                  download
                  class="count-link error--text"
                >
                  <v-icon x-small color="error">mdi-download</v-icon>
                  Download
                </a>
                <span v-else>-</span>
              </template>
            </v-data-table>

            <div class="d-flex flex-wrap justify-end align-center mt-3 history-pagination">
              <span class="text-caption grey--text mr-4">
                {{ historyRangeStart }}-{{ historyRangeEnd }} of {{ historyTotal }}
              </span>
              <span class="text-caption grey--text mr-3">
                Rows per page
              </span>
              <v-select
                v-model="historyLimit"
                :items="[10, 25, 50, 100]"
                dense
                outlined
                hide-details
                class="limit-select mr-4"
                @change="changeHistoryLimit"
              ></v-select>
              <v-pagination
                v-model="historyPage"
                :length="historyTotalPages"
                :total-visible="7"
                :disabled="isHistoryLoading"
                @input="loadHistory"
              ></v-pagination>
            </div>
      </div>
    </v-card>

    <v-dialog v-model="commitDialog" persistent max-width="520">
      <v-card>
        <v-card-title class="subtitleFontsize grey lighten-3">
          Commit sanctions upload
        </v-card-title>
        <v-card-text class="pt-5">
          The file will be validated again before records are written. The
          one-time upload token cannot be reused.
          <div class="mt-3 font-weight-medium">
            {{ validationCounts.INSERT }} insert(s) and
            {{ validationCounts.UPDATE }} update(s) are ready to commit.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small :disabled="isCommitting" @click="commitDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            depressed
            small
            :loading="isCommitting"
            @click="commitUpload"
          >
            Commit upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="recordsDialog" max-width="1200">
      <v-card>
        <v-card-title class="grey lighten-3 py-3">
          <span class="subtitleFontsize">
            {{ selectedRecordStatus }} records
            <span v-if="selectedUploadId">— Upload #{{ selectedUploadId }}</span>
          </span>
          <v-spacer></v-spacer>
          <v-icon @click="recordsDialog = false">mdi-close</v-icon>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="recordsSearch"
            append-icon="mdi-magnify"
            placeholder="Search records"
            dense
            hide-details
            single-line
            class="table-search ml-auto mb-3"
          ></v-text-field>
          <v-data-table
            :headers="recordHeaders"
            :items="recordItems"
            :search="recordsSearch"
            :loading="isRecordsLoading"
            :items-per-page="10"
            fixed-header
            height="55vh"
            loading-text="Loading upload records..."
            no-data-text="No records found"
          ></v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <overlay-page-vue :showOverlay="isCommitting" />
  </section>
</template>

<script>
import BreadCrumb from "@/components/navigations/BreadCrumb.vue";
import alerts from "@/mixins/alerts";
import setupService from "@/services/SetupService/setupService.js";
import OverlayPageVue from "@/utils/OverlayPage.vue";

const STATUS_KEYS = ["INSERT", "UPDATE", "SKIP", "FAIL"];

export default {
  name: "SanctionsBulkUpload",
  components: {
    BreadCrumb,
    OverlayPageVue,
  },
  mixins: [alerts],
  data() {
    return {
      uploadDialog: false,
      valid: true,
      document: null,
      validatedFile: null,
      uploadToken: "",
      previewRows: [],
      previewSearch: "",
      previewStatus: "ALL",
      validationSummary: {},
      failedExcelUrl: "",
      isValidating: false,
      isCommitting: false,
      commitDialog: false,
      historyItems: [],
      historySearch: "",
      historyLimit: 10,
      historyPage: 1,
      historyTotal: 0,
      isHistoryLoading: false,
      recordsDialog: false,
      recordItems: [],
      recordsSearch: "",
      isRecordsLoading: false,
      selectedUploadId: null,
      selectedRecordStatus: "",
      fileRules: [
        (file) => !!file || "Please upload an Excel file",
        (file) => !file || file.size <= 10 * 1024 * 1024 || "File size must be 10 MB or less",
        (file) =>
          !file ||
          /\.(xlsx|xls)$/i.test(file.name || "") ||
          "Only .xlsx or .xls files are allowed",
      ],
      historyHeaders: [
        { text: "Upload ID", value: "upload_id" },
        { text: "Uploaded Date", value: "uploaded_at" },
        { text: "Document Name", value: "document_name" },
        { text: "Uploaded By", value: "uploaded_by" },
        { text: "Total", value: "total_records", align: "end" },
        { text: "Success", value: "no_of_success", align: "end" },
        { text: "Failure", value: "no_of_failure", align: "end" },
        { text: "Upload Type", value: "upload_type" },
        { text: "Failed File", value: "failed_document_path", sortable: false },
      ],
    };
  },
  computed: {
    validationCounts() {
      const counts = { INSERT: 0, UPDATE: 0, SKIP: 0, FAIL: 0 };
      STATUS_KEYS.forEach((status) => {
        const summaryValue = this.readCount(this.validationSummary, status);
        counts[status] =
          summaryValue !== null
            ? summaryValue
            : this.previewRows.filter((row) => row.__status === status).length;
      });
      return counts;
    },
    validationTotal() {
      const explicitTotal = this.firstNumber(this.validationSummary, [
        "total_records",
        "total_rows",
        "total",
        "record_count",
      ]);
      return explicitTotal !== null ? explicitTotal : this.previewRows.length;
    },
    validationCards() {
      return [
        { status: "ALL", label: "Total", count: this.validationTotal, color: "primary" },
        { status: "INSERT", label: "Insert", count: this.validationCounts.INSERT, color: "success" },
        { status: "UPDATE", label: "Update", count: this.validationCounts.UPDATE, color: "info" },
        { status: "SKIP", label: "Skip", count: this.validationCounts.SKIP, color: "warning" },
        { status: "FAIL", label: "Fail", count: this.validationCounts.FAIL, color: "error" },
      ];
    },
    hasValidationResult() {
      return !!this.uploadToken || this.previewRows.length > 0 || Object.keys(this.validationSummary).length > 0;
    },
    canCommit() {
      return (
        !!this.uploadToken &&
        !!this.document &&
        this.document === this.validatedFile &&
        this.hasWritableRows
      );
    },
    hasWritableRows() {
      return this.validationCounts.INSERT + this.validationCounts.UPDATE > 0;
    },
    filteredPreviewRows() {
      if (this.previewStatus === "ALL") return this.previewRows;
      return this.previewRows.filter((row) => row.__status === this.previewStatus);
    },
    previewHeaders() {
      return [
        { text: "Row Number", value: "__row" },
        { text: "Data ID", value: "data_id" },
        { text: "Status", value: "__status" },
        { text: "Remarks", value: "remarks" },
        { text: "UN List Type", value: "un_list_type" },
        { text: "Reference Number", value: "reference_number" },
        { text: "Name", value: "name" },
      ];
    },
    recordHeaders() {
      if (this.selectedRecordStatus === "Successful") {
        return [
          { text: "Data ID", value: "data_id" },
          { text: "Reference Number", value: "reference_number" },
          { text: "Name", value: "__full_name" },
          { text: "UN List Type", value: "un_list_type" },
          { text: "Category", value: "category" },
          { text: "Nationality", value: "nationality" },
          { text: "Designation", value: "designation" },
          { text: "Source", value: "source_of_data" },
          { text: "Listed On", value: "listed_on" },
          { text: "Last Updated", value: "last_day_updated" },
          { text: "Version", value: "version_num", align: "end" },
          { text: "List Type", value: "list_type" },
          { text: "Background", value: "background" },
          { text: "Comments", value: "comments1" },
        ];
      }
      return this.buildDynamicHeaders(this.recordItems, false);
    },
    historyTotalPages() {
      return Math.max(1, Math.ceil(this.historyTotal / this.historyLimit));
    },
    historyRangeStart() {
      if (!this.historyTotal) return 0;
      return (this.historyPage - 1) * this.historyLimit + 1;
    },
    historyRangeEnd() {
      return Math.min(this.historyPage * this.historyLimit, this.historyTotal);
    },
  },
  mounted() {
    this.loadHistory();
  },
  methods: {
    openUploadDialog() {
      this.document = null;
      this.previewSearch = "";
      this.resetValidation(false);
      this.uploadDialog = true;
      this.$nextTick(() => {
        if (this.$refs.bulkUploadForm) {
          this.$refs.bulkUploadForm.resetValidation();
        }
      });
    },
    closeUploadDialog() {
      if (this.isValidating || this.isCommitting) return;
      this.uploadDialog = false;
      this.document = null;
      this.previewSearch = "";
      this.resetValidation(false);
    },
    isSuccessful(response) {
      const code = Number(response && response.data && response.data.status_code);
      return code === 200 || code === 201;
    },
    responseMessage(response, fallback) {
      return (response && response.data && response.data.message) || fallback;
    },
    errorMessage(error, fallback) {
      return (
        (error && error.data && error.data.message) ||
        (error && error.response && error.response.data && error.response.data.message) ||
        (error && error.message) ||
        fallback
      );
    },
    responsePayload(response) {
      return response && response.data ? response.data.data : null;
    },
    firstObject(value) {
      if (Array.isArray(value)) {
        return value.length === 1 && this.isObject(value[0]) ? value[0] : {};
      }
      return this.isObject(value) ? value : {};
    },
    isObject(value) {
      return value !== null && typeof value === "object" && !Array.isArray(value);
    },
    extractArray(value, preferredKeys) {
      if (Array.isArray(value)) {
        if (value.length === 1 && this.isObject(value[0])) {
          const nested = this.extractArrayFromObject(value[0], preferredKeys);
          if (nested) return nested;
        }
        return value;
      }
      if (this.isObject(value)) {
        return this.extractArrayFromObject(value, preferredKeys) || [];
      }
      return [];
    },
    extractArrayFromObject(value, preferredKeys) {
      for (let index = 0; index < preferredKeys.length; index += 1) {
        const candidate = value[preferredKeys[index]];
        if (Array.isArray(candidate)) return candidate;
        if (this.isObject(candidate)) {
          const nested = this.extractArrayFromObject(candidate, preferredKeys);
          if (nested) return nested;
        }
      }
      return null;
    },
    firstValue(object, keys, fallback) {
      for (let index = 0; index < keys.length; index += 1) {
        const value = object && object[keys[index]];
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return fallback;
    },
    firstNumber(object, keys) {
      const value = this.firstValue(object, keys, null);
      if (value === null) return null;
      const number = Number(value);
      return Number.isNaN(number) ? null : number;
    },
    readCount(summary, status) {
      const aliases = {
        INSERT: ["insert", "inserts", "insert_count", "no_of_insert", "to_insert"],
        UPDATE: ["update", "updates", "update_count", "no_of_update", "to_update"],
        SKIP: ["skip", "skips", "skip_count", "no_of_skip", "skipped"],
        FAIL: [
          "fail",
          "fails",
          "fail_count",
          "failure",
          "failure_count",
          "failure_records",
          "failed",
          "failed_count",
          "no_of_failure",
        ],
      };
      return this.firstNumber(summary, aliases[status]);
    },
    normalizeStatus(value) {
      const status = String(value || "").trim().toUpperCase();
      if (status === "FAILED" || status === "FAILURE" || status === "ERROR") return "FAIL";
      if (status === "SKIPPED") return "SKIP";
      if (status === "UPDATED") return "UPDATE";
      if (status === "INSERTED" || status === "NEW") return "INSERT";
      return status;
    },
    normalizePreviewRow(row, index) {
      const item = this.isObject(row) ? row : { value: row };
      return {
        ...item,
        __row: this.firstValue(item, ["row_number", "row_no", "row", "excel_row"], index + 1),
        __status: this.normalizeStatus(
          this.firstValue(item, ["upload_status", "action", "operation", "status", "result"], ""),
        ),
      };
    },
    async validateFile() {
      const formIsValid = this.$refs.bulkUploadForm.validate();
      if (!formIsValid) return;

      this.isValidating = true;
      this.resetValidation(false);
      try {
        const formData = new FormData();
        formData.append("document", this.document);
        const response = await setupService.validateSanctionsBulk(formData);
        if (!this.isSuccessful(response)) {
          this.showAlertError(this.responseMessage(response, "Unable to validate the file."));
          return;
        }

        const payload = this.responsePayload(response);
        const summary = this.firstObject(payload);
        const body = response.data || {};
        this.validationSummary = this.firstObject(
          this.firstValue(summary, ["summary", "counts", "validation_summary"], summary),
        );
        this.uploadToken = this.firstValue(
          summary,
          ["upload_token", "token"],
          this.firstValue(body, ["upload_token", "token"], ""),
        );
        this.failedExcelUrl = this.firstValue(
          summary,
          ["failed_excel_url"],
          "",
        );
        this.previewRows = this.extractArray(payload, [
          "list_of_records",
          "records",
          "preview",
          "rows",
          "items",
          "details",
          "validation_results",
          "results",
          "data",
        ]).map((row, index) => this.normalizePreviewRow(row, index));
        this.validatedFile = this.document;
        this.previewStatus = "ALL";
        this.showAlertSuccess(this.responseMessage(response, "File validated successfully."));
      } catch (error) {
        this.showAlertError(this.errorMessage(error, "Unable to validate the file."));
      } finally {
        this.isValidating = false;
      }
    },
    resetValidation(clearFileReference = true) {
      if (clearFileReference && this.document === this.validatedFile) return;
      this.validatedFile = null;
      this.uploadToken = "";
      this.previewRows = [];
      this.validationSummary = {};
      this.failedExcelUrl = "";
      this.previewStatus = "ALL";
      this.commitDialog = false;
    },
    async commitUpload() {
      if (!this.canCommit) return;
      this.isCommitting = true;
      try {
        const formData = new FormData();
        formData.append("document", this.document);
        const response = await setupService.commitSanctionsBulk(this.uploadToken, formData);
        if (!this.isSuccessful(response)) {
          this.showAlertError(this.responseMessage(response, "Unable to commit the sanctions upload."));
          return;
        }

        this.showAlertSuccess(this.responseMessage(response, "Sanctions file uploaded successfully."));
        this.commitDialog = false;
        this.document = null;
        this.resetValidation(false);
        this.historyPage = 1;
        await this.loadHistory();
        this.uploadDialog = false;
      } catch (error) {
        this.showAlertError(this.errorMessage(error, "Unable to commit the sanctions upload."));
      } finally {
        this.isCommitting = false;
      }
    },
    normalizeHistoryItem(row) {
      const item = this.isObject(row) ? row : {};
      return {
        ...item,
        upload_id: this.firstValue(item, ["upload_id", "id", "document_id"], "-"),
        uploaded_at: this.firstValue(item, ["uploaded_at", "uploaded_date", "created_at", "created_date"], "-"),
        document_name: this.firstValue(item, ["document_name", "file_name", "filename", "document"], "-"),
        document_path: this.firstValue(item, ["document_path", "file_path"], ""),
        uploaded_by: this.firstValue(item, ["uploaded_by", "created_by", "user_name", "login_id"], "-"),
        total_records: this.firstNumber(item, ["total_records", "total_rows", "total", "record_count"]) || 0,
        no_of_success: this.firstNumber(item, ["no_of_success", "success_count", "success"]) || 0,
        no_of_failure: this.firstNumber(item, ["no_of_failure", "failure_count", "failure"]) || 0,
        upload_type: this.firstValue(item, ["upload_type"], "Sanctions Bulk"),
        failed_document_path: this.firstValue(
          item,
          ["failed_document_path", "failed_excel_url"],
          "",
        ),
      };
    },
    async loadHistory() {
      this.isHistoryLoading = true;
      try {
        const response = await setupService.getSanctionsUploadSummary({
          upload_type: "Sanctions Bulk",
          skip: (this.historyPage - 1) * this.historyLimit,
          limit: this.historyLimit,
        });
        if (!this.isSuccessful(response)) {
          this.historyItems = [];
          this.historyTotal = 0;
          this.showAlertError(this.responseMessage(response, "Unable to load upload history."));
          return;
        }

        const payload = this.responsePayload(response);
        const payloadObject = this.firstObject(payload);
        this.historyItems = this.extractArray(payload, [
          "uploads",
          "records",
          "items",
          "results",
          "summaries",
          "data",
        ]).map((row) => this.normalizeHistoryItem(row));
        this.historyTotal = this.firstNumber(payloadObject, [
          "total_count",
          "total_records",
          "count",
        ]) || 0;
      } catch (error) {
        this.historyItems = [];
        this.historyTotal = 0;
        this.showAlertError(this.errorMessage(error, "Unable to load upload history."));
      } finally {
        this.isHistoryLoading = false;
      }
    },
    async openSuccessfulRecords(upload) {
      const uploadId = upload && upload.upload_id;
      if (uploadId === undefined || uploadId === null || uploadId === "-") {
        this.showAlertError("Upload ID is unavailable for this record.");
        return;
      }

      this.selectedUploadId = uploadId;
      this.selectedRecordStatus = "Successful";
      this.recordItems = [];
      this.recordsSearch = "";
      this.recordsDialog = true;
      this.isRecordsLoading = true;
      try {
        const response = await setupService.getSanctionsUploadRecords({
          upload_id: uploadId,
          upload_status: "S",
        });
        if (!this.isSuccessful(response)) {
          this.showAlertError(this.responseMessage(response, "Unable to load upload records."));
          return;
        }
        this.recordItems = this.extractArray(
          this.responsePayload(response),
          ["records", "items", "results", "rows", "details", "data"],
        ).map((record) => this.normalizeUploadRecord(record));
      } catch (error) {
        this.showAlertError(this.errorMessage(error, "Unable to load upload records."));
      } finally {
        this.isRecordsLoading = false;
      }
    },
    buildDynamicHeaders(rows, includeStatus) {
      const headers = [];
      if (includeStatus) {
        headers.push({ text: "Row", value: "__row" });
        headers.push({ text: "Action", value: "__status" });
      }
      const excluded = ["__row", "__status"];
      const keys = [];
      rows.slice(0, 20).forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
          const value = row[key];
          if (
            excluded.indexOf(key) === -1 &&
            keys.indexOf(key) === -1 &&
            (value === null || typeof value !== "object")
          ) {
            keys.push(key);
          }
        });
      });
      keys.slice(0, 14).forEach((key) => {
        headers.push({ text: this.humanize(key), value: key });
      });
      if (!headers.length) headers.push({ text: "Record", value: "value" });
      return headers;
    },
    normalizeUploadRecord(record) {
      const item = this.isObject(record) ? record : { value: record };
      const fullName = [
        item.first_name,
        item.second_name,
        item.third_name,
        item.fourth_name,
      ]
        .filter((namePart) => !!namePart)
        .join(" ");
      return {
        ...item,
        __full_name:
          fullName || item.name_original_script || item.designation || "-",
      };
    },
    humanize(value) {
      return String(value)
        .replace(/^_+/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusColor(statusValue) {
      const status = this.normalizeStatus(statusValue);
      if (status === "INSERT" || status === "SUCCESS" || status === "COMPLETED") return "success";
      if (status === "UPDATE" || status === "PROCESSING" || status === "VALIDATED") return "info";
      if (status === "SKIP" || status === "PENDING") return "warning";
      if (status === "FAIL") return "error";
      return "grey";
    },
    setPreviewStatus(status) {
      this.previewStatus = status;
    },
    changeHistoryLimit() {
      this.historyPage = 1;
      this.loadHistory();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/global.scss";

.sanctions-bulk-upload {
  .sample-file,
  .count-link {
    color: $primary;
    cursor: pointer;
    font-size: 12px;
    text-decoration: none;
  }

  .upload-actions {
    padding-top: 14px;
  }

  .result-card {
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .result-card--active {
    border-color: $primary !important;
    box-shadow: 0 2px 8px rgba(255, 107, 0, 0.15) !important;
  }

  .table-search {
    max-width: 320px;
  }

  .empty-preview {
    background: #fafafa;
    border-style: dashed;
    min-height: 150px;
  }

  .limit-select {
    flex: 0 0 88px;
    max-width: 88px;
  }

  .history-pagination {
    min-height: 38px;
  }
}
</style>
